import React, { useEffect, useRef } from "react";
import {
  useCreateLiveSessionMutation,
  useUpdateLiveSessionMutation,
} from "../../api/api";
import { formatDateTime } from "../../lib/utils";
import { useToast } from "../../@/components/ui/use-toast";
import { ICreateLiveStreamSessionProps } from "../../lib/interfaces";

export const CreateUpdateLiveStreamSession: React.FC<
  ICreateLiveStreamSessionProps
> = ({
  title,
  startDateTime,
  productIds,
  vendorTimezone,
  closeSession,
  isLoading,
  sessionId,
  removeProductIds,
  onSuccess,
  refetchSessionsOnUpdate,
}) => {
  const [createLiveSession] = useCreateLiveSessionMutation();
  const [updateLiveSession] = useUpdateLiveSessionMutation();
  const { toast } = useToast();
  const hasCalledApi = useRef(false);

  useEffect(() => {
    const save = async () => {
      if (hasCalledApi.current) return;
      hasCalledApi.current = true;

      try {
        if (sessionId) {
          const response: any = await updateLiveSession({
            updateLiveSessionDto: {
              id: sessionId,
              title: title,
              startDateTime: formatDateTime(
                startDateTime,
                "YYYY-MM-DDTHH:mm:ssZ"
              ),
              productIds: productIds.length > 0 ? productIds : undefined,
              vendorTimezone: vendorTimezone,
              removeProductIds:
                removeProductIds.length > 0 ? removeProductIds : undefined,
            },
          }).unwrap();

          if (response.message.includes("Live session updated successfully")) {
            toast({
              variant: "success",
              title: "Success",
              description: "Live session rescheduled successfully",
              showIcon: true,
            });
            if (onSuccess) {
              await onSuccess();
            }
          }
        } else {
          const response: any = await createLiveSession({
            createLiveSessionDto: {
              title,
              startDateTime: formatDateTime(
                startDateTime,
                "YYYY-MM-DDTHH:mm:ssZ"
              ),
              vendorTimezone,
              productIds,
            },
          }).unwrap();

          if (response.message.includes("Live session created successfully")) {
            toast({
              variant: "success",
              title: "Success",
              description: "Live session created successfully",
              showIcon: true,
            });
            if (onSuccess) {
              await onSuccess();
            }
            if (refetchSessionsOnUpdate) {
              await refetchSessionsOnUpdate();
            }
          }
        }
      } catch (error) {
        console.error("Error saving live session:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save live session",
          showIcon: true,
        });
      } finally {
        setTimeout(() => {
          closeSession();
        }, 500);
      }
    };

    save();
  }, []);

  return isLoading ? (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-customGreenColor"></div>
        <span className="text-gray-700">
          {sessionId ? "Updating live session..." : "Creating live session..."}
        </span>
      </div>
    </div>
  ) : null;
};
